import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  useDeleteAhuSectionMutation,
  useBulkDeleteSectionsMutation,
} from 'app/services/ahuBoardFormsApi';
import { clearSelection, selectSection } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { pickSectionAfterDelete } from '../sectionReselect';
import { removeErrorsAfterTunnelDeleting } from '../validation/validationController';
import {
  afterNextCanvasRedraw,
  beginCanvasRedrawBatch,
  holdCanvasRedrawBatchFor,
} from '../three/canvasRedrawScheduler';
import { analyticsComponentName, track } from 'app/analytics';

/**
 * section.toolbar.js:413/:315 — the delete cascade's settling delay before
 * `validate:tunnel:after:section:destroy`, which is the pass that takes the
 * section off the canvas (legacy's only one).
 */
const SECTION_DESTROY_DELAY_MS = 500;

/**
 * Highlight the replacement section on the canvas, but only once the pass that
 * takes the deleted one off it has drawn.
 *
 * The redux selection still moves immediately (the panels follow it, and a
 * second Delete press must not be able to re-target a section already being
 * deleted), while the canvas keeps the section the user deleted — still
 * selected — until it actually goes, about a second and a half later. Emitted
 * inline, as it used to be, the board lit the neighbouring section up first
 * and only then removed the deleted one, which reads backwards.
 *
 * The pass itself draws the new selection anyway (a section's canvas object is
 * built `active` from the selection slice), so this is what retargets the
 * tunnel-isolation pill and what covers a delete that redraws nothing.
 */
const selectOnCanvasAfterRedraw = (sectionId: number) => {
  afterNextCanvasRedraw(() => ahuBoardBus.emit('force:select:section', sectionId));
};

/**
 * Phase 6e1 — section delete/duplicate chrome (§8.5, section.toolbar.js +
 * section.toolbar.tmpl, markup faithful): the hidden #duplicate_section /
 * #delete_section buttons (toolbar lis label-forward to them), the Delete
 * Section popup with wording variants (single / multi "Sections" / ER Wheel
 * "both Sections"), Delete-key handler (body keyup 46 outside inputs, gated
 * on `body.ahu-access-permission`), `show:delete:popup` / `show:duplicate:popup`
 * consumers, and the single + bulk delete cascades.
 *
 * [NEG]#43 honored: no error_log_ctrl/section_ctrl change handlers.
 */

interface SectionToolbarChromeProps {
  ahuId: number;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  /** `ahu.is_hybrid` — widens the post-delete reselect to the whole AHU. */
  isHybrid?: boolean;
  /** Opens the insert popup in duplicate mode (§9.2 — InsertSectionPopup). */
  onDuplicateSection: (section: IAhuBoardSection) => void;
}

const SectionToolbarChrome = ({
  ahuId,
  tunnels,
  sections,
  isHybrid,
  onDuplicateSection,
}: SectionToolbarChromeProps) => {
  const dispatch = useAppDispatch();
  const [deleteSection] = useDeleteAhuSectionMutation();
  const [bulkDelete] = useBulkDeleteSectionsMutation();
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const selectedIds = useAppSelector((s) => s.ahuBoard.selection.selectedSectionIds);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupSectionId, setPopupSectionId] = useState<number | null>(null);
  const [dupError, setDupError] = useState<string | null>(null);

  const currentSection = sections.find((s) => s.id === currentSectionId);
  const popupSection = sections.find((s) => s.id === (popupSectionId ?? currentSectionId));
  const multi = selectedIds.length > 1;
  const boundSectionOf = (section: IAhuBoardSection | undefined) =>
    section
      ? sections.find(
          (s) => s.sitting_on_id === section.id || s.id === section.sitting_on_id
        )
      : undefined;

  // Toolbar #sections-ul arming + `.section_info` text (§8.5).
  useEffect(() => {
    const ul = document.getElementById('sections-ul');
    if (!ul) return;
    ul.querySelectorAll('li').forEach((li) =>
      li.classList.toggle('non-clickable', !currentSection)
    );
    ul.querySelectorAll<HTMLElement>('span.section_info').forEach((el) => {
      el.textContent = currentSection
        ? ` : ${String(currentSection.name ?? '')}${
            currentSection.tag ? ` : ${currentSection.tag}` : ''
          }`
        : '';
    });
    ul.querySelector('.section_info_all')?.classList.toggle('hide', !multi);
  }, [currentSection, multi]);

  const showDeletePopup = (sectionId?: number | null) => {
    setPopupSectionId(sectionId ?? null);
    setPopupOpen(true);
    document.querySelector('.main-rotation')?.classList.add('popup-opened');
  };
  const hideDeletePopup = () => {
    setPopupOpen(false);
    setPopupSectionId(null);
    document.querySelector('.main-rotation')?.classList.remove('popup-opened');
  };

  // show:delete:popup / show:duplicate:popup receivers (context menus §2.4).
  useEffect(() => {
    const offs = [
      ahuBoardBus.on('show:delete:popup', (_acc, sectionId) => {
        showDeletePopup(sectionId ?? null);
      }),
      ahuBoardBus.on('show:duplicate:popup', (sectionId) => {
        const section = sections.find((s) => s.id === sectionId);
        if (section) duplicateSection(section);
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // Delete key (body keyup 46 outside inputs/contenteditable, permission-gated).
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Delete') return;
      if (!document.body.classList.contains('ahu-access-permission')) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      )
        return;
      if (currentSectionId != null) showDeletePopup(null);
    };
    document.body.addEventListener('keyup', onKeyUp);
    return () => document.body.removeEventListener('keyup', onKeyUp);
  }, [currentSectionId]);

  /**
   * Server truth for which tunnels this delete left standing.
   *
   * The server drops a tunnel the moment its last section goes
   * (`AHUSection.delete`), so the pre-delete `tunnels` prop can name ids that
   * no longer exist and every per-tunnel GET fired off it 404s. That now
   * includes the AHU's last tunnel, which used to be exempt -- an emptied unit
   * is left with no tunnels at all, and this returns an empty set. Asked rather
   * than derived because a bulk delete empties several tunnels at once and the
   * cascade (a dual-tunnel component's bound peer) reaches tunnels the
   * selection never named. Kicked once per delete and shared, so the
   * section-list refetch and the validation pass agree on one answer.
   */
  const refetchTunnelIds = (): Promise<Set<number>> =>
    dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, {
        forceRefetch: true,
        subscribe: false,
      })
    )
      .unwrap()
      .then((rows) => new Set(rows.map((t) => t.id)))
      // The refetch itself failing says nothing about what survived — fall
      // back to the pre-delete list rather than skipping the rest of the pass.
      .catch(() => new Set(tunnels.map((t) => t.id)));

  // ---- delete cascades (§8.5) ----
  const afterSectionRemoved = (
    section: IAhuBoardSection,
    tunnelRemoved: boolean,
    liveTunnelIds: Promise<Set<number>>
  ) => {
    ahuBoardBus.emit(`section:destroy:${section.id}`);
    ahuBoardBus.emit('select:accessory', null);
    ahuBoardBus.emit(`remove_component_esp_${section.id}`);
    if (tunnelRemoved) {
      ahuBoardBus.emit('sections_collection:removed', section.tunnel_id);
      ahuBoardBus.emit('tunnel:removed', section.tunnel_id);
      ahuBoardBus.emit('render_tunnel_lugs');
      void dispatch(removeErrorsAfterTunnelDeleting(section.tunnel_id));
      ahuBoardBus.emit('get:overall:height', true);
    }
    window.setTimeout(() => {
      ahuBoardBus.emit('validate:tunnel:after:section:destroy', {
        sectionId: section.id,
        tunnelId: section.tunnel_id,
        skip: true,
        tunnel_removed: tunnelRemoved,
      });
      // Only for a tunnel that is still there. When this delete took the
      // tunnel's last section the server deleted the tunnel with it, so
      // GET tunnels/<id>/components/validation would 404 — and there is
      // nothing to validate anyway, its errors having just been dropped by
      // `removeErrorsAfterTunnelDeleting` above.
      void liveTunnelIds.then((ids) => {
        if (!ids.has(section.tunnel_id)) return;
        ahuBoardBus.emit('validate:tunnel:components:only', {
          tunnelId: section.tunnel_id,
        });
      });
    }, SECTION_DESTROY_DELAY_MS);
  };

  /**
   * @param emptied this delete took the AHU's last section, so the unit row
   * changed too: the server clears `is_hybrid` once nothing is left
   * (api/rest_ahu/utils.py `reset_hybrid_mode_if_empty`), and the insert strip,
   * the ADD Section popup, the lug refetch and the AHU tab all read that flag.
   * Only refetched then — any other delete leaves `is_hybrid` alone.
   */
  const refetchBoard = (liveTunnelIds: Promise<Set<number>>, emptied = false) => {
    // Server truth after a delete: the tunnel row (recalculated length) and
    // every tunnel's section list — the server shifts `order_rate` on
    // everything downstream of the gap, and the canvas numbers sections off it
    // (canvas3dGeometry:629). Fetched directly rather than through
    // invalidateTags (same two endpoints, ids matched) so the delete's single
    // canvas pass can wait for them and draw the shifted numbering first time.
    // The tunnel list leads (it IS `liveTunnelIds`) and the section lists are
    // asked for only once it says which tunnels the delete left standing —
    // off the stale prop this fired a GET for a tunnel the server had just
    // deleted with its last section, which came back 404.
    holdCanvasRedrawBatchFor(
      Promise.all([
        liveTunnelIds.then((ids) =>
          Promise.all(
            [...ids].map((id) =>
              dispatch(
                ahuBoardApi.endpoints.getTunnelSectionList.initiate(id, {
                  forceRefetch: true,
                  subscribe: false,
                })
              )
            )
          )
        ),
        ...(emptied
          ? [
              dispatch(
                ahuBoardApi.endpoints.getAhuUnit.initiate(ahuId, {
                  forceRefetch: true,
                  subscribe: false,
                })
              ),
            ]
          : []),
      ])
    );
  };

  const doDelete = () => {
    document.querySelector('.deleting-section-note')?.classList.add('active');
    // Close the popup straight away — the delete request runs in the background
    // and the "deleting section..." note is what reports it still being in
    // flight. Everything below reads values captured before this point.
    hideDeletePopup();
    // One canvas pass for the whole delete (canvasRedrawScheduler). Without
    // it the optimistic cache filter redraws the section away while the
    // tunnel around it is still the old length, each re-GET redraws again,
    // and the ported `validate:tunnel:after:section:destroy` pass redraws a
    // third time 500 ms later. Legacy is data-blind and only ever runs that
    // last one (section.toolbar.js:413-425), which is what this restores —
    // the section stays put for the half second, then goes in one clean draw.
    const releaseCanvas = beginCanvasRedrawBatch();
    // One tunnel-list refetch for the whole delete, memoized: the cascade
    // (which tunnels may still be validated) and `refetchBoard` (which section
    // lists to re-GET) both need it, and both must read the same answer.
    let tunnelIdsAfter: Promise<Set<number>> | null = null;
    const liveTunnelIds = () => (tunnelIdsAfter ??= refetchTunnelIds());
    const finish = (emptied = false) => {
      document.querySelector('.deleting-section-note')?.classList.remove('active');
      refetchBoard(liveTunnelIds(), emptied);
      // Released just after the destroy pass is requested: `.then` above runs
      // before this `.finally`, so at an equal delay its timer fires first.
      window.setTimeout(releaseCanvas, SECTION_DESTROY_DELAY_MS);
    };

    if (multi) {
      // Bulk delete — collect bound dual ids too (§8.5 / parity 163).
      const ids = new Set<number>(selectedIds);
      selectedIds.forEach((id) => {
        const bound = boundSectionOf(sections.find((s) => s.id === id));
        if (bound) ids.add(bound.id);
      });
      const sectionList = [...ids]
        .map((id) => sections.find((s) => s.id === id))
        .filter((s): s is IAhuBoardSection => !!s);
      const tunnelIds = new Set(sectionList.map((s) => s.tunnel_id));
      const remaining = sections.filter((s) => !ids.has(s.id));
      // Re-select first remaining or full deselect. Done before the request
      // (as legacy does, section.toolbar.js:368) so that with the popup already
      // closed a second Delete press can't re-target a section being deleted.
      if (remaining.length) {
        dispatch(selectSection({ sectionId: remaining[0].id }));
        selectOnCanvasAfterRedraw(remaining[0].id);
      } else {
        dispatch(clearSelection());
        ahuBoardBus.emit('remove:selected:section');
      }
      ahuBoardBus.emit('update:multiple:sections:list');
      bulkDelete({ section_ids: [...ids], tunnel_ids: [...tunnelIds] })
        .unwrap()
        .then(() => {
          track('section_bulk_deleted', { count: ids.size });
          sectionList.forEach((s) => {
            const tunnelEmpty = !remaining.some((r) => r.tunnel_id === s.tunnel_id);
            afterSectionRemoved(s, tunnelEmpty, liveTunnelIds());
          });
        })
        .catch(() => undefined)
        // `remaining` is what the server leaves: `tunnel_ids` only scopes
        // `section_ids`, so exactly the ids we named go (#1654). Before that fix
        // the whole tunnel went and this had to assume the board was emptied.
        .finally(() => finish(remaining.length === 0));
      return;
    }

    const section = popupSection;
    if (!section) {
      finish();
      return;
    }
    const tunnelSections = sections.filter((s) => s.tunnel_id === section.tunnel_id);
    const bound = boundSectionOf(section);
    // Airflow-aware neighbor pick (order−2 / order — §8.5), widened to the
    // AHU's whole position order on a hybrid unit, where the deleted section is
    // usually its tunnel's only one (sectionReselect.ts).
    const neighbor = pickSectionAfterDelete({
      sections,
      tunnels,
      deleted: section,
      alsoDeletedIds: [bound?.id],
      isHybrid,
    });

    // Selection moves off the section before the request (legacy ordering,
    // section.toolbar.js:368) — see the bulk path above.
    if (neighbor && neighbor.id !== section.id) {
      dispatch(selectSection({ sectionId: neighbor.id }));
      selectOnCanvasAfterRedraw(neighbor.id);
    } else {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }

    deleteSection({ id: section.id, tunnelId: section.tunnel_id })
      .unwrap()
      .then(() => {
        track('section_deleted', { section_type: analyticsComponentName(section.type) });
        const tunnelEmpty = tunnelSections.length <= 1;
        afterSectionRemoved(section, tunnelEmpty, liveTunnelIds());
        if (bound) {
          // The bound peer's tunnel can go the same way (the server cascades
          // the pair, so that tunnel can be emptied by this one request) —
          // then its whole error entry goes with it (`tunnel_removed`, which
          // legacy passed as `!sections[sitting_id_tunnel]`,
          // section.toolbar.js:421-425) and there is nothing left to
          // re-validate, so `skip` the GET the row no longer answers.
          // Without the flag the entry survived the tunnel: a
          // `do_not_generate` error on a section the delete had just removed
          // stayed in the Error Manager and kept Generate disabled.
          void liveTunnelIds().then((ids) => {
            const boundTunnelRemoved = !ids.has(bound.tunnel_id);
            ahuBoardBus.emit('validate:tunnel:after:section:destroy', {
              // The peer section is the one this cascade destroyed — naming
              // `bound.sitting_on_id` pointed at the section the user deleted
              // whenever the peer was the half holding the link.
              sectionId: bound.id,
              tunnelId: bound.tunnel_id,
              skip: boundTunnelRemoved,
              tunnel_removed: boundTunnelRemoved,
            });
          });
        }
      })
      .catch(() => undefined)
      // Counting the bound peer out too — the server cascades it with the
      // section it sits on, so it goes in this same request.
      .finally(() =>
        finish(!sections.some((s) => s.id !== section.id && s.id !== bound?.id))
      );
  };

  const duplicateSection = (section: IAhuBoardSection) => {
    const bound = boundSectionOf(section);
    if (bound) {
      // dual-tunnel sections → error popup (§8.5 / parity 164).
      setDupError(String(section.name ?? ''));
      return;
    }
    onDuplicateSection(section);
  };

  return (
    <>
      <div className={`delete-duplicate-wrapper${currentSection ? ' active' : ''}`}>
        <button
          id="duplicate_section"
          data-id={currentSection?.id}
          className={`hide icon-copy ahu-header-buttons  btn-green ka-has-tooltip${
            boundSectionOf(currentSection) ? ' non-clickable' : ''
          }`}
          {...{ tooltip: 'Duplicate' }}
          onClick={() => currentSection && duplicateSection(currentSection)}
        />
        <button
          id="delete_section"
          data-id={currentSection?.id}
          className="hide icon-trash ahu-header-buttons  btn-red ka-has-tooltip"
          {...{ tooltip: 'Delete' }}
          onClick={() => showDeletePopup(null)}
        />
      </div>

      <div className={`popup-wrapper delete-section-popup width-small${popupOpen ? ' active' : ''}`}>
        <div className="popup-content">
          <h2 className="popup-heading">Delete Section?</h2>
          <div>
            <div className="clearfix popup-content-part">
              {/* section.toolbar.js:199-211 — three texts: multi / "ER Wheel" /
                  single (double space after "Component" verbatim). */}
              <p>
                {multi
                  ? 'If you choose to Delete, Cabinet, Component & Accessories Data of the Sections will be lost and will not be recoverable.'
                  : String(popupSection?.name ?? '') === 'ER Wheel'
                    ? 'If you choose to Delete, Cabinet, Component & Accessories Data of both Sections will be lost and will not be recoverable.'
                    : 'If you choose to Delete, Cabinet, Component  & Accessories Data of the Section will be lost and will not be recoverable.'}
              </p>
              {/* section.toolbar.js:183-197 — UPPERCASED name + tag; EMPTY on
                  multi-select. */}
              <span className="section-tag-value">
                {multi
                  ? ''
                  : popupSection
                    ? `${String(popupSection.name ?? '').toUpperCase()}${
                        popupSection.tag ? ` : ${popupSection.tag}` : ''
                      }`
                    : ''}
              </span>
            </div>
            <div className="popup-control-buttons">
              <button
                className="delete not-highlighted-btn"
                data-id={popupSection?.id}
                name="delete_section_btn"
                onClick={doDelete}
              >
                DELETE
              </button>
              <button
                className="default-btn highlighted-btn"
                name="cancel_delete_section_btn"
                onClick={hideDeletePopup}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* "Can't duplicate dual-tunnel sections." (error_insert_component.popup.tmpl). */}
      {dupError != null && (
        <div className="popup-wrapper width-small active">
          <div className="popup-content">
            <h2 className="popup-heading">Duplicate Section</h2>
            <div>
              <div className="clearfix popup-content-part">
                <p>Can&apos;t duplicate dual-tunnel sections.</p>
                <span className="section-tag-value">{dupError} </span>
              </div>
              <div className="popup-control-buttons">
                <button
                  className="default-btn btn cancel"
                  name="cancel"
                  onClick={() => setDupError(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SectionToolbarChrome;
